import axios from "axios";
import { handleAuthError, getAuthHeaders } from "../../../utils/authUtils";
import { API_BASE_URL } from "../../../environment";

const checkSalesUserRole = (): {
  isSalesUser: boolean;
  isClientAdmin: boolean;
  userId: number | null;
} => {
  let isSalesUser = false;
  let isClientAdmin = false;
  let userId = null;

  const authResponse = localStorage.getItem("authResponse");

  if (authResponse) {
    try {
      const auth = JSON.parse(authResponse);

      const tenantId = localStorage.getItem("selectedTenantId");

      const tenantObj = Array.isArray(auth.tenants)
        ? auth.tenants.find((t: any) => String(t.tenant_id) === tenantId)
        : null;

      isSalesUser = tenantObj?.role_key === "SALESUSER";
      isClientAdmin = tenantObj?.role_key === "CLIENTADMIN";
      userId = auth.user_id || null;
    } catch (e) {
      console.warn("Could not parse auth response to check for user role", e);
    }
  }

  return { isSalesUser, isClientAdmin, userId };
};

const getLeadUrl = () => {
  const tenantId = localStorage.getItem("selectedTenantId");

  if (!tenantId) throw new Error("Tenant ID not found in localStorage.");

  const { isSalesUser, userId } = checkSalesUserRole();

  let url = `${API_BASE_URL}/lead/?tenant_id=${tenantId}`;

  if (isSalesUser && userId) {
    url += `&sales_user_id=${userId}`;
  } else {
  }

  return url;
};

const addTenantToLead = <T extends { tenant_id?: number | null }>(
  lead: Partial<T>
): Partial<T> => {
  const tenantId = localStorage.getItem("selectedTenantId");
  if (!tenantId) {
    throw new Error("Tenant ID not found in localStorage.");
  }

  const authResponse = localStorage.getItem("authResponse");
  let currentUserId = null;

  if (authResponse) {
    try {
      const auth = JSON.parse(authResponse);
      currentUserId = auth.user_id;
    } catch (e) {
      console.warn("Could not parse auth response for user_id");
    }
  }

  return {
    ...lead,
    tenant_id: parseInt(tenantId, 10),
    created_by: currentUserId,
    updated_by: currentUserId,
  };
};

export interface Lead {
  lead_id?: number;
  crm_entity_id?: number;
  title: string;
  company_id?: number | null;
  source?: string;
  lifecycle_source_id?: number | null;
  lifecycle_source?: {
    lifecycle_source_id: number;
    lifecycle_source_key: string;
    lifecycle_source_display_name: string;
  };
  score: number | null;
  preferred_language: string;
  time_zone: string;
  created_datetime: string | null;
  updated_datetime: string | null;
  created_by: {
    user_id: number;
    full_name: string;
    avatar_url: string;
  } | null;
  updated_by: {
    user_id: number;
    full_name: string;
    avatar_url: string;
  } | null;
  assigned_user_id: number | null;
  assigned_datetime: string | null;
  estimated_value: number | null;
  currency_code: string;
  priority?: string | null;
  products_n_services?: string | null;
  primary_contact: {
    contact_id: number;
    primary_email: string;
    personal_email: string;
    primary_phone: string;
    mobile_phone: string;
    preferred_language: string;
    address_line1: string;
    address_line2: string;
    source: string;
    city: string;
    state: string;
    country: string;
    avatar_url: string | null;
    full_name: string;
    job_title: string;
  } | null;
  tenant_id: number | null;
  lifecycle_type_stage_id?: number | null;
  company?: {
    company_id: number;
    company_name: string;
    business_type_display_name: string;
    industry_type_display_name: string;
    avatar_url: string | null;
    address: string;
  };
  assigned_user?: {
    user_id: number;
    full_name: string;
    email: string;
    phone: string | null;
    avatar_url: string;
  };
  lifecycle_type_stage?: {
    lifecycle_type_stage_id: number;
    stage_name: string;
  };
  assigned_team?: {
    team_id: number;
    name?: string;
    team_name?: string;
    manager?: {
      user_id: number;
      full_name: string;
      avatar_url: string;
    };
  };
  is_active?: boolean;
  is_archive?: boolean;
  lifecycle_status_reason?: {
    lifecycle_status_reason_id: number;
    reason_name: string;
    lifecycle_status_reason_category_key: string;
  } | null;
  tags?: {
    insert: { entity_tag_id: number; entity_tag_name: string }[];
  } | null;
  product_n_service?: any[];
}

export interface LeadRequest {
  lead_id?: number;
  title: string;
  company_id?: number | null;
  source?: string;
  lifecycle_source_id?: number | null;
  score: number | null;
  preferred_language: string;
  time_zone: string;
  estimated_value: number | null;
  currency_code: string;
  priority?: string | null;
  products_n_services?: string | null;
  primary_contact: number | null;
  lifecycle_type_stage_id: number | null;
  assigned_user_id: number | null;
  tenant_id: number | null;
  created_by?: number | null;
  updated_by?: number | null;
  product_n_service?: any[];
}

export interface PaginatedLeadResponse {
  leads: Lead[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  hasMore: boolean;
  pageSize: number;
}

export const LeadService = {
  getLeadsByPage: async (
    page: number = 1,
    pageSize: number = 20,
    filters?: Record<string, any>,
    lightMode: boolean = false
  ): Promise<PaginatedLeadResponse> => {
    try {
      const params: Record<string, any> = {
        page: page,
        page_size: pageSize,
      };

      if (lightMode) {
        params.response_mode = "light";
      }

      // Handle archived filter
      if (filters && filters.archivedFilter) {
        if (filters.archivedFilter === "yes") {
          params.is_archive = true;
        } else if (filters.archivedFilter === "no") {
          params.is_archive = false;
        }
        // For "both", don't add is_archive parameter
      } else {
        // Default to non-archived if no filter specified
        params.is_archive = false;
      }

      if (filters) {
        if (filters.searchTerm) {
          params.title = filters.searchTerm;
        }
        if (filters.dateRange && filters.dateRange[0]) {
          params.created_datetime_gte = filters.dateRange[0]
            .toISOString()
            .split("T")[0];
        }
        if (filters.dateRange && filters.dateRange[1]) {
          params.created_datetime_lte = filters.dateRange[1]
            .toISOString()
            .split("T")[0];
        }
        if (filters.statusFilter) {
          params.lifecycle_type_stage_id = filters.statusFilter;
        }

        Object.keys(filters).forEach((key) => {
          if (!["searchTerm", "dateRange", "statusFilter", "archivedFilter", "sortBy", "sortOrder", "industryFilter", "countryFilter"].includes(key)) {
            const val = filters[key];
            if (val !== undefined && val !== null && val !== "") {
              params[key] = val;
            }
          }
        });
      }

      const response = await axios.get(getLeadUrl(), {
        headers: getAuthHeaders(),
        params: params,
      });

      let leads: Lead[] = [];
      let totalCount = 0;

      if (response.data.results) {
        leads = response.data.results;
        totalCount = response.data.count || leads.length;
      } else if (Array.isArray(response.data)) {
        leads = response.data;
        totalCount = leads.length;
      } else {
        console.warn("Unexpected API response format:", response.data);
        return {
          leads: [],
          totalCount: 0,
          totalPages: 0,
          currentPage: page,
          hasMore: false,
          pageSize: pageSize,
        };
      }

      const totalPages = Math.ceil(totalCount / pageSize);
      const hasMore = page < totalPages;

      return {
        leads,
        totalCount,
        totalPages,
        currentPage: page,
        hasMore,
        pageSize: pageSize,
      };
    } catch (error) {
      console.error("Error fetching leads page:", error);
      return handleAuthError(error);
    }
  },

  getAllLeads: async (): Promise<Lead[]> => {
    try {
      const response = await LeadService.getLeadsByPage(1, 1000, undefined, true);
      return response.leads;
    } catch (error) {
      console.error("Error fetching leads:", error);
      return handleAuthError(error);
    }
  },

  getLeadById: async (id: number): Promise<Lead> => {
    try {
      const tenantId = localStorage.getItem("selectedTenantId");

      let url = `${API_BASE_URL}/lead/${id}/?tenant=${tenantId}`;

      const { isSalesUser, userId } = checkSalesUserRole();

      if (isSalesUser && userId) {
        url += `&sales_user_id=${userId}`;
      } else {
      }

      const response = await axios.get(url, {
        headers: getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching lead:", error);
      return handleAuthError(error);
    }
  },

  getLeadLookup: async (id: number, tenantId: number) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/lookup/lead/?lead_id=${id}&tenant_id=${tenantId}`,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching lead lookup:", error);
      throw error;
    }
  },

  createLead: async (lead: Partial<LeadRequest>): Promise<Lead> => {
    try {
      const leadWithTenant = addTenantToLead(lead);
      const response = await axios.post(
        `${API_BASE_URL}/lead/`,
        leadWithTenant,
        {
          headers: getAuthHeaders(),
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error creating lead:", error);
      return handleAuthError(error);
    }
  },
  updateLead: async (id: number, lead: Partial<LeadRequest>): Promise<Lead> => {
    try {
      const leadWithTenant = addTenantToLead(lead);

      const { created_by, ...updateData } = leadWithTenant;
      const response = await axios.patch(
        `${API_BASE_URL}/lead/${id}/`,
        updateData,
        {
          headers: getAuthHeaders(),
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error updating lead:", error);
      return handleAuthError(error);
    }
  },

  deleteLead: async (id: number): Promise<void> => {
    try {
      const tenantId = localStorage.getItem("selectedTenantId");

      let url = `${API_BASE_URL}/lead/${id}/?tenant=${tenantId}`;

      const { isSalesUser, userId } = checkSalesUserRole();

      if (isSalesUser && userId) {
        url += `&sales_user_id=${userId}`;
      } else {
      }

      await axios.delete(url, {
        headers: getAuthHeaders(),
      });
    } catch (error) {
      console.error("Error deleting lead:", error);
      return handleAuthError(error);
    }
  },

  getCrmEntityId: async (): Promise<number | undefined> => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/crm-entity/?crm_entity_key=LEAD`,
        { headers: getAuthHeaders() }
      );
      if (response.data?.results?.length > 0) {
        return response.data.results[0].crm_entity_id;
      }
    } catch (error) {
      console.error("Error fetching CRM entity ID:", error);
      return undefined;
    }
  },

  evaluateEmailEngagement: async (tenantIdOverride?: number): Promise<void> => {
    try {
      let tenantId = tenantIdOverride;
      if (!tenantId) {
        const tenantIdStr = localStorage.getItem("selectedTenantId");
        if (tenantIdStr) {
          tenantId = parseInt(tenantIdStr, 10);
        }
      }
      if (!tenantId) return;

      const crmEntityId = await LeadService.getCrmEntityId();
      if (!crmEntityId) return;

      await axios.post(
        `${API_BASE_URL}/crm-entity-email-engagement/evaluate/`,
        {
          tenant_id: tenantId,
          crm_entity_id: crmEntityId,
        },
        { headers: getAuthHeaders() }
      );
    } catch (error) {
      console.error("Error evaluating email engagement:", error);
    }
  },
};
